import { ITEM_LOCATION_TYPES, ROADMAP_TYPES } from '@/roadmaps/constants/roadmaps.constants';
import { calculateRoadmapTimeData } from '@/roadmaps/helpers/roadmaps-date.helper';
import each from 'lodash/each';
import has from 'lodash/has';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import omit from 'lodash/omit';

export function freezeRoadmap (roadmap) {
    delete roadmap.items;
    delete roadmap.swimlanes;

    return Object.freeze({
        ...roadmap,
    });
}

export function roadmapObjectFromResponse (roadmapResponseData) {
    const swimlanes = multipleSwimlaneObjectsFromResponse(roadmapResponseData.swimlanes);

    switch (roadmapResponseData.type) {
        case ROADMAP_TYPES.TIMELINE: {
            const { useRoadmapsStore } = require('@/stores/roadmaps.store');
            const { currentRoadmap } = useRoadmapsStore();

            let roadmapStartDate = currentRoadmap?.startDate;
            let roadmapEndDate = currentRoadmap?.endDate;
            let timePeriodLabels = currentRoadmap?.timePeriodLabels;

            const alreadyHasTimeData = !!roadmapStartDate && !!roadmapEndDate && !!timePeriodLabels;
            if (!alreadyHasTimeData) {
                const {
                    roadmapStartDate: newRoadmapStartDate,
                    roadmapEndDate: newRoadmapEndDate,
                    timePeriodLabels: newTimePeriodLabels,
                } = calculateRoadmapTimeData({
                    items: roadmapResponseData.items,
                    quarterOneStartingMonth: roadmapResponseData.quarterOneStartingMonth,
                });

                roadmapStartDate = newRoadmapStartDate;
                roadmapEndDate = newRoadmapEndDate;
                timePeriodLabels = newTimePeriodLabels;
            }

            const items = roadmapResponseData.items?.length
                ? multipleItemObjectsFromResponse(roadmapResponseData.items, roadmapResponseData.type)
                : [];

            return Object.assign({}, roadmapResponseData, {
                timePeriodLabels,
                startDate: roadmapStartDate,
                endDate: roadmapEndDate,
                swimlanes,
                items,
            });
        }

        case ROADMAP_TYPES.COLUMN: {
            const columns = multipleColumnObjectsFromResponse(roadmapResponseData.columns);

            const items = roadmapResponseData.items?.length
                ? multipleItemObjectsFromResponse(roadmapResponseData.items, roadmapResponseData.type)
                : [];

            return Object.assign({}, roadmapResponseData, {
                swimlanes,
                columns,
                items,
            });
        }
    }
}

export function multipleSwimlaneObjectsFromResponse (responseSwimlanes) {
    return keyBy(
        map(responseSwimlanes, (swimlane, index) => {
            return {
                ...swimlane,
                index,
            };
        }),
        'id',
    );
}

export function multipleColumnObjectsFromResponse (responseColumns) {
    return keyBy(
        map(responseColumns, (column, index) => {
            return {
                ...column,
                index,
            };
        }),
        'id',
    );
}

export function singleColumnObjectFromResponse (responseColumn) {
    const { useRoadmapsColumnsStore } = require('@/stores/roadmaps-columns.store');
    const existingColumn = useRoadmapsColumnsStore().columnById(responseColumn.id);

    return {
        ...responseColumn,
        index: existingColumn?.index ?? responseColumn.index ?? 0,
    };
}

export function multipleItemObjectsFromResponse (
    responseItems,
    roadmapType,
) {
    if (roadmapType === undefined) {
        const { useRoadmapsStore } = require('@/stores/roadmaps.store');
        roadmapType = useRoadmapsStore().currentRoadmap?.type;
    }
    const parentChildCount = {};

    const items = keyBy(
        map(responseItems, (item) => {
            const isChild = item.parentType === ITEM_LOCATION_TYPES.INITIATIVE.key;

            if (isChild) {
                parentChildCount[item.parentId] ||= 0;
                parentChildCount[item.parentId] += 1;
            }

            const { useRoadmapsItemsStore } = require('@/stores/roadmaps-items.store');

            return {
                ...(useRoadmapsItemsStore().itemById(item.id) || {}),
                ...singleItemObjectFromResponse(item, roadmapType),
            };
        }),
        'id',
    );

    return each(items, (item) => {
        const hasChildren = (parentChildCount[item.id] || 0) > 0;
        if (!hasChildren || !has(item, 'isExpanded')) {
            item.isExpanded = false;
        }
    });
}

export function singleItemObjectFromResponse (
    responseItem,
    roadmapType,
) {
    if (roadmapType === undefined) {
        const { useRoadmapsStore } = require('@/stores/roadmaps.store');
        roadmapType = useRoadmapsStore().currentRoadmap?.type;
    }
    switch (roadmapType) {
        case ROADMAP_TYPES.TIMELINE: {
            const { useRoadmapsItemsStore } = require('@/stores/roadmaps-items.store');
            const isCurrentlyExpanded = !!useRoadmapsItemsStore().itemById(responseItem.id)?.isExpanded;
            const isExpanded = isCurrentlyExpanded && responseItem.parentType === ITEM_LOCATION_TYPES.SWIMLANE.key;

            return Object.assign({}, responseItem, {
                isExpanded,
            });
        }
        case ROADMAP_TYPES.COLUMN:
            return omit(responseItem, ['startDate', 'endDate']);
    }
}

export function payloadWithRoadmapVersionTimestamp (payload) {
    const { useRoadmapsStore } = require('@/stores/roadmaps.store');
    const roadmapVersionTimestamp = useRoadmapsStore().currentRoadmapVersionTimestamp;

    return {
        roadmapVersionTimestamp,
        ...payload,
    };
}

export function updateRoadmapCreatedUpdatedInfoFromResponse (roadmapCreatedUpdatedInfo) {
    const { useRoadmapsStore } = require('@/stores/roadmaps.store');
    useRoadmapsStore().setCurrentRoadmapCreatedUpdatedInfo(roadmapCreatedUpdatedInfo);
}

export function updateRoadmapPublishedInfoFromResponse (publishedInfo) {
    const { useRoadmapsStore } = require('@/stores/roadmaps.store');
    useRoadmapsStore().setCurrentRoadmapPublishInfo(publishedInfo);
}
